import { useLazyQuery } from '@apollo/client'
import {
    Container,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import IntakeCard from 'candidate/components/IntakeCard'
import type { ChoiceBucket } from 'candidate/models/intake'
import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { GetCompanyOffices } from 'recruiter/graphql/companies.gql'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import NextButton from 'shared/components/NextButton'
import { getPxAsRem } from 'shared/utils'

export interface InternshipAnswer {
    company: (ChoiceBucket & { id: string }) | undefined
    industries: ChoiceBucket[] | undefined
    sectors: ChoiceBucket[] | undefined
    companyOffice: ChoiceBucket | undefined
    returnOffer: boolean | undefined
    returnReason: string
}
interface Props {
    answer?: InternshipAnswer
    callback: Dispatch<any>
    industryChoices: ChoiceBucket[]
    sectorChoices: ChoiceBucket[]
    prompt: string
    skipCallback?: Dispatch<SetStateAction<string>>
    skipText?: string
}

const InternshipQuestion: FC<Props> = ({
    answer,
    callback,
    industryChoices,
    sectorChoices,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [disabled, setDisabled] = useState<boolean>(true)
    const [response, setResponse] = useState<InternshipAnswer>({
        company: answer?.company || undefined,
        industries: answer?.industries || undefined,
        sectors: answer?.sectors || undefined,
        companyOffice: answer?.companyOffice || undefined,
        returnOffer: answer?.returnOffer || undefined,
        returnReason: answer?.returnReason || '',
    })

    const [companyOffices, setCompanyOffices] = useState<ChoiceBucket[]>([])

    // Set up an on-demand query for company office locations.
    const [getCompanyOffices, { loading: isOfficeLocationDataLoading }] =
        useLazyQuery(GetCompanyOffices, {
            onCompleted: (data) => {
                if (data.company.value in companyOffices) return

                const companyOfficeChoices =
                    data.company.companyOffices?.map(({ officeLocation }) => ({
                        choice: officeLocation?.value ?? '',
                    })) ?? []

                setCompanyOffices(companyOfficeChoices)
            },
        })

    // Pre-fill the form with any existing answers, fetching dependent values
    // as needed.
    useEffect(() => {
        if (!answer) return

        if (answer.company) {
            getCompanyOffices({ variables: { companyId: answer.company.id } })
        }

        setResponse(answer)
    }, [answer, getCompanyOffices])

    useEffect(() => {
        if (
            response.company &&
            (response.industries?.length ?? 0) > 0 &&
            (response.sectors?.length ?? 0) > 0 &&
            response.companyOffice &&
            response.returnOffer !== undefined &&
            response.returnReason !== ''
        ) {
            setDisabled(false)
        } else {
            setDisabled(true)
        }
    }, [response])

    const submitData = () => {
        callback({ response })
    }

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            <Container
                sx={{
                    display: 'inline-block',
                    alignContent: 'left',
                    marginBottom: getPxAsRem(36),
                    marginTop: getPxAsRem(16),
                    padding: '0',
                    width: '100%',
                    maxWidth: '100%',
                }}
            >
                <CompaniesAutocomplete
                    sx={{ marginBottom: getPxAsRem(24) }}
                    value={
                        response.company
                            ? {
                                  id: response.company.id,
                                  value: response.company.choice,
                              }
                            : null
                    }
                    onChange={(e, nextValue) => {
                        if (nextValue?.id && nextValue?.value) {
                            setResponse({
                                ...response,
                                company: {
                                    id: nextValue.id,
                                    choice: nextValue.value,
                                },
                            })
                        }
                        // fetch officeLocations for the selected company
                        if (nextValue?.id) {
                            getCompanyOffices({
                                variables: { companyId: nextValue?.id },
                            })
                        }
                    }}
                />

                <FormControl
                    sx={{
                        marginBottom: getPxAsRem(24),
                        '&:has(.Mui-disabled)': {
                            opacity: 0.5,
                            cursor: 'not-allowed',
                        },
                    }}
                    fullWidth
                >
                    <Autocomplete
                        multiple
                        id="industries"
                        options={industryChoices}
                        getOptionLabel={(option) => option.choice}
                        value={response.industries || []}
                        onChange={(event, newValue) => {
                            setResponse({
                                ...response,
                                industries: newValue,
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Industries"
                                placeholder="Select industries"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.choice}
                                    {...getTagProps({ index })}
                                    key={option.choice}
                                />
                            ))
                        }
                    />
                </FormControl>

                <FormControl sx={{ marginBottom: getPxAsRem(24) }} fullWidth>
                    <Autocomplete
                        multiple
                        id="sectors"
                        options={sectorChoices}
                        getOptionLabel={(option) => option.choice}
                        value={response.sectors || []}
                        onChange={(event, newValue) => {
                            setResponse({
                                ...response,
                                sectors: newValue,
                            })
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Sectors"
                                placeholder="Select sectors"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.choice}
                                    {...getTagProps({ index })}
                                    key={option.choice}
                                />
                            ))
                        }
                    />
                </FormControl>

                <FormControl sx={{ marginBottom: getPxAsRem(24) }} fullWidth>
                    <InputLabel id="office">Office Location</InputLabel>
                    <Select
                        id="office"
                        labelId="office_label"
                        label="Office Location"
                        value={response.companyOffice?.choice || ''}
                        disabled={
                            !response.company ||
                            isOfficeLocationDataLoading ||
                            companyOffices.length === 0
                        }
                        onChange={(e) => {
                            const companyOffice = companyOffices.find(
                                (choiceBucket) =>
                                    choiceBucket.choice === e.target.value
                            )

                            setResponse({
                                ...response,
                                companyOffice,
                            })
                        }}
                    >
                        {companyOffices.map((choiceBucket, index) => (
                            <MenuItem
                                key={`${choiceBucket.choice}_${index}`}
                                value={choiceBucket.choice}
                            >
                                {choiceBucket.choice}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ marginBottom: getPxAsRem(24) }} fullWidth>
                    <FormLabel id="return_offer-group-label">
                        Did you receive a return offer?
                    </FormLabel>
                    <RadioGroup
                        name={'radio-btn-group'}
                        defaultValue={() => {
                            if (
                                response &&
                                response.returnOffer !== undefined
                            ) {
                                if (response.returnOffer === true) {
                                    return 'Yes'
                                } else {
                                    return 'No'
                                }
                            }
                        }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const returnOffer =
                                event.target.value === 'Yes' ? true : false
                            setResponse({ ...response, returnOffer })
                        }}
                    >
                        {['Yes', 'No'].map((choice, index) => (
                            <FormControlLabel
                                key={index}
                                value={choice}
                                control={<Radio />}
                                label={choice}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>

                <FormControl sx={{ marginBottom: getPxAsRem(24) }} fullWidth>
                    <TextField
                        id="return"
                        label="Why or why not?"
                        value={response.returnReason}
                        multiline
                        rows={7}
                        onChange={(e) => {
                            setResponse({
                                ...response,
                                returnReason: e.target.value,
                            })
                        }}
                    />
                </FormControl>
            </Container>

            <NextButton disabled={disabled} onClickHandler={submitData} />
        </IntakeCard>
    )
}

export default InternshipQuestion
